// src/components/ExistingUserJoin.js
import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * ExistingUserJoin Component
 * Handles existing user joining entity via invitation
 * Requires password verification for security
 */
const ExistingUserJoin = ({ existingUser, invitation, email, onJoinEntity, submitting, onCancel }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const displayEmail = email || invitation.email || existingUser?.email;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setPasswordError(t('validation.passwordRequired'));
      return;
    }
    
    setPasswordError('');
    onJoinEntity(password);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (passwordError) {
      setPasswordError('');
    }
  };

  return (
    <div className="max-w-md w-full space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">
          {t('welcomeBack')}
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {t('foundExistingAccount')}
        </p>
      </div>

      {/* Existing User Info Card */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {t('yourAccount')}
            </h3>
            <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
              {(existingUser?.first_name || existingUser?.last_name) && (
                <p><strong>{t('name')}:</strong> {existingUser.first_name} {existingUser.last_name}</p>
              )}
              {existingUser?.username && (
                <p><strong>{t('username')}:</strong> {existingUser.username}</p>
              )}
              {displayEmail && (
                <p><strong>{t('email')}:</strong> {displayEmail}</p>
              )}
              
              {existingUser?.entities && existingUser.entities.length > 0 && (
                <div className="mt-2">
                  <p><strong>{t('currentOrganizations')}:</strong></p>
                  <ul className="list-disc list-inside ml-2">
                    {existingUser.entities.map((entity, index) => (
                      <li key={index} className="text-xs">
                        {entity.entity_name} ({entity.role})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Join Entity Info */}
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
              {t('joinOrganization')}
            </h3>
            <div className="mt-2 text-sm text-green-700 dark:text-green-300">
              <p><strong>{t('organization')}:</strong> {invitation.entityName}</p>
              <p><strong>{t('role')}:</strong> {invitation.role}</p>
              {invitation.invitationMessage && (
                <p><strong>{t('message')}:</strong> {invitation.invitationMessage}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Password Verification Form */}
      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {t('verifyYourIdentity')}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {t('enterPasswordToJoin', { entityName: invitation.entityName })}
          </p>
          <p className="text-medium text-gray-700 dark:text-gray-300 mb-4">
            {t('email')}:  {displayEmail}
          </p>
          <div>            
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('password')} * 
            </label>
            <div className="relative mt-1">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={handlePasswordChange}
                className={`w-full px-3 py-2 pr-10 border ${
                  passwordError ? 'border-red-300' : 'border-gray-300'
                } rounded-md focus:ring-blue-500 focus:border-blue-500 placeholder-gray-500 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                placeholder={t('enterYourPassword')}
                disabled={submitting}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
                disabled={submitting}
              >
                {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
              </button>
            </div>
            {passwordError && (
              <p className="mt-1 text-sm text-red-600">{passwordError}</p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-4">
          <button
            type="submit"
            disabled={submitting}
            className={`flex-1 flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
              submitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
            }`}
          >
            {submitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('joining')}
              </>
            ) : (
              t('joinEntity', { entityName: invitation.entityName })
            )}
          </button>
          
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            {t('notYou')}
          </button>
        </div>

        {/* Help Text */}
        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('joinPermissionsNotice', { entityName: invitation.entityName, role: invitation.role })}            
          </p>
        </div>
      </form>
    </div>
  );
};

export default ExistingUserJoin;
